import { useTranslation } from '../../../shared/i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CountryFlag, { normalizeCountryCode } from '../../../shared/components/CountryFlag';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Alert, Button, Card, Col, Collapse, Drawer, Empty, Grid, Input, InputNumber, Modal, Radio, Row, Segmented, Select, Slider, Space, Spin,
    Switch, Tag, Tooltip, Typography, message, theme,
} from 'antd';
import { CheckCircleFilled, EnvironmentOutlined, UpOutlined } from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';
import PageHeader from '../../../shared/components/PageHeader';
import Money from '../../../shared/components/Money';
import { PLATFORM_CURRENCY } from '../../../shared/utils/money';
import { userPath } from '../../../shared/config/paths';
import { generatePassword } from '../../../shared/components/evm/vmdetail/utils';
import SshKeyPicker, { EMPTY_SSH_PICK, sshPickPayload, sshPickValid } from '../../../shared/components/evm/SshKeyPicker';
import { osFamily } from '../../../assets/os';

const { Text } = Typography;

const api = {
    get: async (url, params) => {
        try { return (await apiClient.get(url, { params })).data; } catch (e) { return { status: 'error', msg: e.response?.data?.msg || e.message }; }
    },
    post: async (url, data) => {
        try { return (await apiClient.post(url, data)).data; } catch (e) { return { status: 'error', msg: e.response?.data?.msg || e.message }; }
    },
};
const ok = (r) => r && r.status === 'success';

const gb = (mb) => `${+(Number(mb) / 1024).toFixed(2)} GB`;
const fmtValue = (key, v) => (key === 'memory' ? gb(v) : v);
const HOSTNAME_RE = /^[A-Za-z0-9][A-Za-z0-9-]{0,62}$/;
/** Plan resources tuned inside the plan section; the rest are add-ons. */
const ADDON_KEYS = ['data_disk_size', 'public_ip_num'];

/** "中文|English" plan names: pick the side for the UI language. */
const localName = (name, lang) => {
    const parts = String(name || '').split('|').map((s) => s.trim()).filter(Boolean);
    if (parts.length < 2) return name;
    return /^zh/i.test(lang || '') ? parts[0] : parts[1];
};

/** ISO country code → SVG flag (no emoji: Windows doesn't render flag emoji). */
const flag = (code, size) => (normalizeCountryCode(code) ? <CountryFlag code={code} size={size} /> : null);

const cmpDesc = (a, b) => String(b.name).localeCompare(String(a.name), undefined, { numeric: true, sensitivity: 'base' });

/** Numbered step card. */
const Step = ({ n, title, extra, children, compact }) => {
    const { token } = theme.useToken();
    return (
        <Card
            size="small"
            style={{ marginBottom: 12 }}
            styles={{ body: { padding: compact ? 12 : 16 } }}
            title={(
                <Space size={8}>
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%',
                        background: token.colorPrimary, color: token.colorTextLightSolid, fontSize: 12, fontWeight: 600,
                    }}
                    >
                        {n}
                    </span>
                    <span>{title}</span>
                </Space>
            )}
            extra={extra}
        >
            {children}
        </Card>
    );
};

/** Selectable card. */
const Pick = ({ active, disabled, onClick, children, style }) => {
    const { token } = theme.useToken();
    const go = () => { if (!disabled) onClick(); };
    return (
        <div
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-pressed={active}
            aria-disabled={disabled}
            onClick={go}
            onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); go(); } }}
            style={{
                position: 'relative',
                cursor: disabled ? 'not-allowed' : 'pointer',
                padding: active ? 11 : 12,
                height: '100%',
                borderRadius: token.borderRadiusLG,
                border: `${active ? 2 : 1}px solid ${active ? token.colorPrimary : token.colorBorderSecondary}`,
                background: disabled ? token.colorFillQuaternary : active ? token.colorPrimaryBg : token.colorBgContainer,
                opacity: disabled ? 0.6 : 1,
                transition: 'border-color .15s, background .15s',
                ...style,
            }}
        >
            {active && <CheckCircleFilled style={{ position: 'absolute', top: 8, right: 8, color: token.colorPrimary }} />}
            {children}
        </div>
    );
};

/** One labelled spec line in a plan card. */
const SpecLine = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, lineHeight: '20px' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
        <Text style={{ fontSize: 12, textAlign: 'right' }}>{value}</Text>
    </div>
);

/** Slider + number for one adjustable resource. */
const ResourceRow = ({ r, value, onChange, extra, t, narrow }) => (
    <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span>
                <Text>{t(r.label)}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}> · {t('Incl. {{v}}', { v: `${fmtValue(r.key, r.included)}${r.key === 'memory' ? '' : ` ${t(r.unit)}`}` })}</Text>
            </span>
            {extra}
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: narrow ? 'wrap' : 'nowrap' }}>
            <Slider
                style={{ flex: 1, minWidth: 140 }}
                min={r.min}
                max={r.max}
                step={r.step}
                value={value}
                tooltip={{ formatter: (v) => fmtValue(r.key, v) }}
                onChange={onChange}
            />
            <InputNumber
                min={r.min}
                max={r.max}
                step={r.step}
                value={value}
                onChange={(v) => v !== null && onChange(v)}
                addonAfter={t(r.unit)}
                style={{ width: 150 }}
            />
        </div>
    </div>
);

const PurchaseVm = ({ kind: routeKind }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n?.language;
    const navigate = useNavigate();
    const { token } = theme.useToken();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const wide = !!screens.lg; // sidebar summary vs bottom bar
    const [searchParams] = useSearchParams();
    const legacyVds = !routeKind && searchParams.get('kind') === 'vds'; // old /evm/vms/purchase?kind=vds links
    const kind = routeKind === 'vds' ? 'vds' : 'cloud';

    const [catalog, setCatalog] = useState(null);
    const [areaId, setAreaId] = useState(null);
    const [plan, setPlan] = useState(null); // {productId, templateId}
    const [opts, setOpts] = useState(null);
    const [optsError, setOptsError] = useState(null);
    const [optsLoading, setOptsLoading] = useState(false);
    const [imageId, setImageId] = useState(null);
    const [famPick, setFamPick] = useState({}); // OS family key → chosen image id
    const [values, setValues] = useState({});
    const [ipv6, setIpv6] = useState(0); // extra IPv6 block length (0 = none)
    const [period, setPeriod] = useState(null);
    const [hostname, setHostname] = useState('');
    const [auth, setAuth] = useState('password');
    const [password, setPassword] = useState(() => generatePassword(14));
    const [sshPick, setSshPick] = useState(EMPTY_SSH_PICK);
    const [quote, setQuote] = useState(null);
    const [quoting, setQuoting] = useState(false);
    const [balance, setBalance] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [sgs, setSgs] = useState([]);
    const [sgIds, setSgIds] = useState([]);
    // network: public IPv4 (1/0) + VPC none | existing | new
    const [publicV4, setPublicV4] = useState(1);
    const [vpcMode, setVpcMode] = useState('none');
    const [subnetId, setSubnetId] = useState(null);
    const [vpcName, setVpcName] = useState('');
    const [vpcCidr, setVpcCidr] = useState('');
    const [detailOpen, setDetailOpen] = useState(false);

    useEffect(() => {
        api.get('/api/evm/user/order/catalog').then((r) => {
            setCatalog(ok(r) ? r.data : { areas: [], products: [] });
        });
        api.get('/api/user/evm/security-group').then((r) => {
            const list = ok(r) ? r.data?.security_groups || [] : [];
            setSgs(list);
            setSgIds(list.filter((g) => g.is_default).map((g) => g.id));
        });
        api.get('/api/user/balance').then((r) => {
            const b = r?.data?.balance;
            if (b !== undefined) setBalance(Number(b));
        });
    }, []);

    const hasVds = (catalog?.products || []).some((p) => p.kind === 'vds');
    const products = useMemo(() => (catalog?.products || []).filter((p) => (p.kind || 'cloud') === kind), [catalog, kind]);
    // every catalog area, with its state for this category: ok | soldout | none
    const areas = useMemo(() => (catalog?.areas || []).map((a) => ({
        ...a,
        state: products.some((p) => p.areas.includes(a.id)) ? 'ok'
            : products.some((p) => (p.soldout_areas || []).includes(a.id)) ? 'soldout' : 'none',
    })), [catalog, products]);
    const openAreas = useMemo(() => areas.filter((a) => a.state === 'ok'), [areas]);
    useEffect(() => {
        if (!openAreas.some((a) => a.id === areaId)) setAreaId(openAreas[0]?.id ?? null);
    }, [openAreas, areaId]);
    const area = areas.find((a) => a.id === areaId);

    // plans of the area; one that no node there can take now stays listed, greyed 资源不足 (soldout)
    const plans = useMemo(() => products
        .filter((p) => p.areas.includes(areaId) || (p.soldout_areas || []).includes(areaId))
        .flatMap((p) => p.plans
            .filter((pl) => !pl.areas || pl.areas.includes(areaId) || (pl.soldout_areas || []).includes(areaId))
            .map((pl) => ({ ...pl, soldout: !!pl.areas && !pl.areas.includes(areaId), kind: p.kind, productId: p.id, productName: p.name }))), [products, areaId]);

    // keep the selected plan valid for the area (never a sold-out one)
    useEffect(() => {
        const open = plans.filter((p) => !p.soldout);
        if (!open.length) { setPlan(null); return; }
        if (!plan || !open.some((p) => p.productId === plan.productId && p.id === plan.templateId)) {
            setPlan({ productId: open[0].productId, templateId: open[0].id });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plans]);
    const planRow = plans.find((p) => plan && p.productId === plan.productId && p.id === plan.templateId);

    // plan/area → options from the node the server will use
    useEffect(() => {
        if (!plan || !areaId) { setOpts(null); setOptsError(null); return undefined; }
        let stale = false; // a newer plan/area was picked before this answer came back
        setOptsLoading(true);
        setOptsError(null);
        api.get('/api/evm/user/order/options', { product_id: plan.productId, template_id: plan.templateId, area_id: areaId }).then((r) => {
            if (stale) return;
            setOptsLoading(false);
            if (!ok(r)) { setOpts(null); setOptsError(r?.msg || t('Failed to load')); return; }
            // tagged with the plan/area it belongs to, so no quote mixes the new plan with the old plan's values
            setOpts({ ...r.data, forKey: `${plan.productId}:${plan.templateId}:${areaId}` });
            const v = {};
            r.data.resources.forEach((x) => { v[x.key] = x.included; });
            setValues(v);
            setIpv6(0);
            const vpc = r.data.vpc || {};
            const subnets = (vpc.vpcs || []).flatMap((x) => x.subnets.map((sn) => sn.id));
            const mode = (m) => ((m === 'existing' && !subnets.length) || (m === 'new' && !vpc.can_create) || !vpc.available ? 'none' : m);
            setVpcMode((m) => { const nextMode = mode(m); if (nextMode === 'none') setPublicV4(1); return nextMode; });
            setSubnetId((cur) => (subnets.includes(cur) ? cur : subnets[0] ?? null));
            setVpcCidr((cur) => cur || vpc.suggest_cidr || '');
            setImageId((cur) => {
                if (r.data.images.some((i) => i.id === cur && !i.unavailable)) return cur;
                // default: newest Ubuntu, then Debian, then the first image the plan can run
                const sorted = [...r.data.images].filter((i) => !i.unavailable).sort(cmpDesc);
                const pref = ['ubuntu', 'debian'].map((k) => sorted.find((i) => osFamily(i).key === k)).find(Boolean);
                return (pref || sorted[0])?.id ?? null;
            });
            setPeriod((cur) => ((cur === 'hour' && r.data.hourly) || r.data.cycles.some((c) => c.period === cur) ? cur : r.data.cycles[0]?.period ?? null));
        });
        return () => { stale = true; };
    }, [plan, areaId, t]);

    const orderInput = useCallback(() => ({
        product_id: plan?.productId,
        template_id: plan?.templateId,
        area_id: areaId,
        image_id: imageId,
        period,
        ...values,
        ipv6_len: publicV4 ? ipv6 || 0 : 0,
        public_ipv4: publicV4,
        vpc: vpcMode,
        ...(vpcMode === 'existing' ? { subnet_id: subnetId } : {}),
        ...(vpcMode === 'new' ? { vpc_name: vpcName || undefined, vpc_cidr: vpcCidr || undefined } : {}),
    }), [plan, areaId, imageId, period, values, ipv6, publicV4, vpcMode, subnetId, vpcName, vpcCidr]);

    // live server quote (the only price shown)
    useEffect(() => {
        if (!opts || !imageId || !period || opts.forKey !== `${plan?.productId}:${plan?.templateId}:${areaId}`) { setQuote(null); return undefined; }
        let stale = false;
        setQuoting(true);
        const h = setTimeout(async () => {
            const r = await api.post('/api/evm/user/order/quote', orderInput());
            if (stale) return; // the inputs changed meanwhile; a newer quote is on its way
            setQuoting(false);
            setQuote(ok(r) ? r.data : { error: r?.msg || t('Unable to quote') });
        }, 300);
        return () => { stale = true; clearTimeout(h); };
    }, [opts, imageId, period, orderInput, plan, areaId, t]);

    // images grouped into OS families (newest version first)
    const families = useMemo(() => {
        const map = new Map();
        (opts?.images || []).forEach((img) => {
            const f = osFamily(img);
            if (!map.has(f.key)) map.set(f.key, { ...f, images: [] });
            map.get(f.key).images.push(img);
        });
        return [...map.values()].map((f) => ({ ...f, images: [...f.images].sort(cmpDesc) }));
    }, [opts]);

    // image minimums (server: ImageRequirements): greyed with one line when the chosen memory is below
    const memNow = Number(values.memory ?? planRow?.memory ?? 0);
    const imgBlock = useCallback((i) => {
        if (i.unavailable) return t(i.unavailable);
        const min = Number(i.min_memory_mb) || 0;
        return min > 0 && memNow > 0 && memNow < min ? t('Needs ≥{{n}} RAM', { n: `${+(min / 1024).toFixed(1)}G` }) : null;
    }, [memNow, t]);
    useEffect(() => {
        // memory lowered below the chosen image's minimum: move to the first image that fits
        const cur = opts?.images.find((i) => i.id === imageId);
        if (cur && imgBlock(cur)) {
            const next = [...opts.images].sort(cmpDesc).find((i) => !imgBlock(i));
            setImageId(next?.id ?? null);
        }
    }, [opts, imageId, imgBlock]);
    const image = opts?.images.find((i) => i.id === imageId);
    const imageFamily = image ? osFamily(image).key : null;
    const isWindows = image?.type === 'windows' || /windows/i.test(image?.name || '');
    const loginUser = image?.username || (isWindows ? 'Administrator' : 'root');
    const sshMode = auth === 'ssh' && !isWindows;
    const hostnameOk = !hostname || HOSTNAME_RE.test(hostname);
    const passwordOk = password.length >= 8 && password.length <= 64;
    const authOk = sshMode ? sshPickValid(sshPick) : passwordOk;
    const subnetOk = vpcMode !== 'existing' || !!subnetId;
    const total = quote && !quote.error ? Number(quote.total) : null;
    // 报价为平台定价币种，余额为余额币种：用后端折算的 total_in_balance 比较（缺失则交给后端判定）
    const totalInBalance = quote && !quote.error && quote.total_in_balance != null ? Number(quote.total_in_balance) : null;
    const bal = quote && !quote.error && quote.balance != null ? Number(quote.balance) : balance;
    const balCurrency = (quote && !quote.error && quote.balance_currency) || PLATFORM_CURRENCY;
    const short = totalInBalance !== null && bal !== null && bal < totalInBalance;

    // first reason the order can't go yet (one line under the button)
    const blocker = (() => {
        if (!openAreas.length) return null;
        if (optsError) return optsError;
        if (!plan) return t('Select a plan');
        if (!opts) return null;
        if (!opts.images.length) return t('No images available in this region');
        if (!imageId) return t('Select an image');
        if (!authOk) return sshMode ? t('Select or paste an SSH public key') : t('Password must be 8-64 characters');
        if (!subnetOk) return t('Select a subnet');
        if (!hostnameOk) return t('Invalid instance name');
        if (quote?.error) return quote.error;
        if (short) return t('Insufficient balance');
        return null;
    })();
    const blockerIsError = !!(short || quote?.error || optsError);
    const canBuy = total !== null && !quoting && !blocker && !submitting;

    const buy = async () => {
        setSubmitting(true);
        const data = { ...orderInput(), expected_total: total, hostname: hostname || undefined, security_group_ids: sgIds };
        if (sshMode) {
            Object.assign(data, sshPickPayload(sshPick));
        } else {
            data.password = password;
        }
        const r = await api.post('/api/evm/user/vms', data);
        setSubmitting(false);
        if (ok(r) && r.data?.pending) {
            message.success(t('Paid, provisioning'));
            navigate(userPath('/evm/vms'));
        } else if (ok(r) && r.data?.vm?.id) {
            message.success(t('Paid, instance is being created'));
            navigate(userPath(`/evm/vms/${r.data.vm.id}`));
        } else {
            message.error(r?.msg || r?.error || t('Purchase failed'));
            if ((r?.msg || '').includes('价格')) setQuote(null);
        }
    };

    const CYCLE = { 1: t('Monthly billing'), 3: t('Quarterly'), 6: t('Every 6 months'), 12: t('Yearly'), 24: t('Every 2 years'), 36: t('Every 3 years') };
    const cycleLabel = (p) => (p === 'hour' ? t('Hourly') : CYCLE[Number(p)] || t('{{n}} months', { n: p }));
    const payg = quote && !quote.error ? quote.payg : null;

    const confirmBuy = () => {
        Modal.confirm({
            title: t('Confirm purchase'),
            content: (
                <div>
                    <div>{localName(planRow?.name, lang)} · {area?.name} · {image?.name} · {cycleLabel(period)}</div>
                    <div style={{ marginTop: 8 }}>
                        {t('Amount due')} <Text strong><Money amount={total} currency={PLATFORM_CURRENCY} /></Text>
                        {totalInBalance !== null && balCurrency !== PLATFORM_CURRENCY && (
                            <Text type="secondary"> ≈ <Money amount={totalInBalance} currency={balCurrency} /></Text>
                        )}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{payg ? t('Prepays {{n}} hours from your balance, then keeps topping up automatically. Stops if the balance runs out; unused whole hours are refunded when you delete it.', { n: payg.hours }) : t('Charged to your balance')}</Text>
                </div>
            ),
            okText: t('Pay now'),
            cancelText: t('Cancel'),
            onOk: buy,
        });
    };

    if (legacyVds) return <Navigate to={userPath('/evm/vds')} replace />;
    if (!catalog) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;

    const visibleRes = (opts?.resources || []).filter((r) => r.adjustable && !(r.key === 'public_ip_num' && !publicV4));
    const planRes = visibleRes.filter((r) => !ADDON_KEYS.includes(r.key));
    const addonRes = visibleRes.filter((r) => ADDON_KEYS.includes(r.key));
    const extraTag = (key) => {
        const x = (quote?.extras || []).find((e) => e.key === key);
        return x ? <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>+<Money amount={x.amount} currency={PLATFORM_CURRENCY} /></Text> : null;
    };
    const setVal = (key) => (v) => setValues((s) => ({ ...s, [key]: v }));
    const monthly = opts?.cycles.find((c) => Number(c.period) === 1);
    const v6Available = !!opts?.ipv6?.available && publicV4 === 1;
    const vpcList = opts?.vpc?.vpcs || [];
    const subnetName = vpcList.flatMap((v) => v.subnets.map((sn) => ({ id: sn.id, label: `${v.name} · ${sn.cidr}` }))).find((x) => x.id === subnetId)?.label;
    const selPlan = plans.find((p) => p.productId === plan?.productId && p.id === plan?.templateId);
    const natPlan = selPlan?.nat || null;
    const netSummary = [
        publicV4 ? (natPlan ? t('NAT · {{f}} forwards · {{s}} sites', { f: natPlan.forwards, s: natPlan.sites }) : t('Public IPv4')) : t('No public IPv4'),
        vpcMode === 'new' ? t('New VPC') : vpcMode === 'existing' ? (subnetName || 'VPC') : t('No VPC'),
    ].filter(Boolean).join(' · ');

    let stepNo = 0;
    const nextStep = () => { stepNo += 1; return stepNo; };
    const col4 = { xs: 12, sm: 8, xl: 6 };

    // ---------------------------------------------------------------- summary (sidebar / mobile drawer)
    const line = (label, amount, key) => (
        <div key={key || label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0' }}>
            <Text type="secondary" ellipsis style={{ minWidth: 0 }}>{label}</Text>
            <Text style={{ whiteSpace: 'nowrap' }}><Money amount={amount} currency={PLATFORM_CURRENCY} /></Text>
        </div>
    );
    const summaryBody = (
        <div>
            <Space direction="vertical" size={2} style={{ width: '100%', marginBottom: 8 }}>
                {area && <Text>{flag(area.code)}{area.name}</Text>}
                {planRow && <Text strong>{localName(planRow.name, lang)}</Text>}
                {image && <Text type="secondary" ellipsis style={{ fontSize: 12, maxWidth: '100%' }}>{image.name}</Text>}
                {period && <Text type="secondary" style={{ fontSize: 12 }}>{cycleLabel(period)}</Text>}
            </Space>
            {payg && (
                <div style={{ borderTop: `1px dashed ${token.colorBorderSecondary}`, paddingTop: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0' }}>
                        <Text type="secondary">{t('Hourly')}</Text>
                        <Text style={{ whiteSpace: 'nowrap' }}><Money amount={payg.rate} currency={PLATFORM_CURRENCY} decimals={4} /></Text>
                    </div>
                    {payg.traffic_per_gb > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0' }}>
                            <Text type="secondary">{t('Public outbound traffic')}</Text>
                            <Text style={{ whiteSpace: 'nowrap' }}><Money amount={payg.traffic_per_gb} currency={PLATFORM_CURRENCY} decimals={4} />{t('/GB')}</Text>
                        </div>
                    )}
                    {quote.setup > 0 && line(t('Setup fee'), quote.setup, 'setup')}
                </div>
            )}
            {quote && !quote.error && !payg && (
                <div style={{ borderTop: `1px dashed ${token.colorBorderSecondary}`, paddingTop: 8 }}>
                    {line(t('Plan'), quote.cycle_price, 'plan')}
                    {(quote.extras || []).map((x) => line(
                        x.key === 'ipv6' ? x.label : `${t(x.label)} +${fmtValue(x.key, x.qty)}${x.key === 'memory' ? '' : ` ${t(x.unit)}`}`,
                        x.amount,
                        x.key,
                    ))}
                    {quote.setup > 0 && line(t('Setup fee'), quote.setup, 'setup')}
                </div>
            )}
            {quote?.ipv4_bundled_unused > 0 && <Text type="secondary" style={{ fontSize: 12 }}>{t('IPv4 is included in the plan price; no discount')}</Text>}
            {quote?.sys_disk_raised_from > 0 && (
                <div><Text type="secondary" style={{ fontSize: 12 }}>{t('System disk set to {{n}} GB as required by the image', { n: quote.values?.sys_disk_size })}</Text></div>
            )}
        </div>
    );
    const totalBlock = (
        <div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                <Text type="secondary">{t('Total')}</Text>
                <Text strong style={{ fontSize: 22, color: token.colorPrimary, whiteSpace: 'nowrap' }}>
                    {quoting ? <Spin size="small" /> : total !== null ? <Money amount={total} currency={PLATFORM_CURRENCY} /> : '—'}
                </Text>
            </div>
            {payg && (
                <div style={{ textAlign: 'right' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>{t('First {{n}} hours prepaid', { n: payg.hours })}</Text>
                </div>
            )}
            {total !== null && Number(period) > 1 && (
                <div style={{ textAlign: 'right' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>≈ <Money amount={total / Number(period)} currency={PLATFORM_CURRENCY} />{t('/month')}</Text>
                </div>
            )}
        </div>
    );
    const balanceLine = bal !== null && (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>{t('Balance')} <Money amount={bal} currency={balCurrency} /></Text>
            {short && <a style={{ fontSize: 12 }} onClick={() => navigate(userPath('/billing/balance'))}>{t('Top up')}</a>}
        </div>
    );
    const buyButton = (
        <Button type="primary" size="large" block disabled={!canBuy} loading={submitting} onClick={confirmBuy}>
            {t('Buy now')}
        </Button>
    );

    // ---------------------------------------------------------------- sections
    const kindSwitch = (
        <Segmented
            block={isMobile}
            value={kind}
            onChange={(k) => navigate(userPath(k === 'vds' ? '/evm/vds' : '/evm/vms/purchase'), { replace: true })}
            options={[
                { value: 'cloud', label: t('Standard cloud server') },
                { value: 'vds', label: <Tooltip title={t('Dedicated physical cores')}>VDS</Tooltip> },
            ]}
        />
    );

    const regionStep = (
        <Step n={nextStep()} title={t('Region')} compact={isMobile}>
            <Row gutter={[10, 10]}>
                {areas.map((a) => {
                    const reason = a.state === 'soldout' ? t('Insufficient resources') : a.state === 'none' ? (kind === 'vds' ? t('No VDS') : t('No plans')) : null;
                    return (
                        <Col key={a.id} {...col4}>
                            <Pick active={a.id === areaId} disabled={!!reason} onClick={() => setAreaId(a.id)}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 16 }}>
                                    <span style={{ fontSize: 22, lineHeight: 1, display: 'inline-flex' }}>{flag(a.code, 'lg') || <EnvironmentOutlined />}</span>
                                    <Text strong ellipsis style={{ minWidth: 0 }}>{a.name}</Text>
                                </div>
                                <Text type={reason ? 'secondary' : 'success'} style={{ fontSize: 12 }}>{reason || t('Available')}</Text>
                            </Pick>
                        </Col>
                    );
                })}
            </Row>
        </Step>
    );

    const planStep = (
        <Step n={nextStep()} title={t('Plan')} compact={isMobile}>
            <Row gutter={[10, 10]}>
                {plans.map((p) => {
                    const active = plan?.productId === p.productId && plan?.templateId === p.id;
                    const natLine = p.nat ? t('NAT · {{f}} forwards · {{s}} sites', { f: p.nat.forwards, s: p.nat.sites }) : null;
                    const ips = [natLine, p.public_ip_num ? `${p.public_ip_num} IPv4` : null, p.ipv6_prefix_len && (!p.v6_areas || p.v6_areas.includes(areaId)) ? `${p.ipv6_count > 1 ? `${p.ipv6_count} × ` : ''}IPv6 /${p.ipv6_prefix_len}` : null].filter(Boolean).join(' · ');
                    return (
                        <Col key={`${p.productId}-${p.id}`} xs={24} sm={12} xl={8}>
                            <Pick active={active} disabled={p.soldout} onClick={() => setPlan({ productId: p.productId, templateId: p.id })}>
                                <div style={{ paddingRight: 20, marginBottom: 6 }}>
                                    <Text strong>{localName(p.name, lang)}</Text>
                                    {p.kind === 'vds' && <Tag color="gold" style={{ marginLeft: 6 }}>{t('Dedicated')}</Tag>}
                                    {p.elastic && <Tag style={{ marginLeft: 6 }}>{t('Adjustable')}</Tag>}
                                    {p.nat && <Tooltip title={t('Shares the node\'s public IPv4; reached through port forwards and website forwards')}><Tag color="cyan" style={{ marginLeft: 6 }}>NAT</Tag></Tooltip>}
                                </div>
                                <SpecLine label="CPU" value={p.kind === 'vds' ? t('{{n}} dedicated cores', { n: p.core }) : t('{{n}} vCPU', { n: p.core })} />
                                <SpecLine label={t('Memory')} value={gb(p.memory)} />
                                <SpecLine label={t('Disk')} value={`${p.sys_disk_size} GB${p.data_disk_size ? ` + ${p.data_disk_size} GB` : ''}`} />
                                <SpecLine label={t('Bandwidth')} value={p.net_out ? `${p.net_out} Mbps` : t('Unmetered')} />
                                <SpecLine label={t('Traffic')} value={p.flow_limit ? t('{{n}} GB/mo', { n: p.flow_limit }) : t('Unlimited')} />
                                {ips && <SpecLine label="IP" value={ips} />}
                                <div style={{ marginTop: 8 }}>
                                    <Text strong style={{ fontSize: 18, color: token.colorPrimary }}><Money amount={p.from} currency={PLATFORM_CURRENCY} /></Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}> {t('/month and up')}</Text>
                                    {p.soldout && <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>{t('Insufficient resources')}</Text>}
                                </div>
                            </Pick>
                        </Col>
                    );
                })}
            </Row>
            {optsError && <Alert type="warning" showIcon style={{ marginTop: 12 }} message={optsError} />}
            {planRes.length > 0 && (
                <Space direction="vertical" size={14} style={{ width: '100%', marginTop: 16 }}>
                    {planRes.map((r) => (
                        <ResourceRow key={r.key} r={r} t={t} narrow={isMobile} value={values[r.key]} onChange={setVal(r.key)} extra={extraTag(r.key)} />
                    ))}
                </Space>
            )}
        </Step>
    );

    const imageStep = opts && (
        <Step n={nextStep()} title={t('Image')} compact={isMobile}>
            {!families.length ? <Text type="warning">{t('No images available in this region')}</Text> : (
                <Row gutter={[10, 10]}>
                    {families.map((f) => {
                        const active = f.key === imageFamily;
                        const usable = f.images.filter((i) => !imgBlock(i));
                        const famBlock = usable.length ? null : imgBlock(f.images[0]);
                        const kept = famPick[f.key];
                        const chosen = active ? imageId : (usable.some((i) => i.id === kept) ? kept : (usable[0] || f.images[0]).id);
                        const win = /windows/i.test(f.key);
                        const pick = (id) => { setFamPick((s) => ({ ...s, [f.key]: id })); setImageId(id); };
                        return (
                            <Col key={f.key} {...col4}>
                                <Pick active={active} disabled={!!famBlock} onClick={() => pick(chosen)}>
                                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, paddingRight: 16, marginBottom: 8 }}>
                                        <span style={{
                                            width: 30, height: 30, borderRadius: 8, background: '#fff', border: `1px solid ${token.colorBorderSecondary}`,
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none',
                                        }}
                                        >
                                            <img src={f.logo} alt="" width={20} height={20} />
                                        </span>
                                        <Text strong style={{ minWidth: 0, lineHeight: 1.25, wordBreak: 'break-word' }}>{f.name}</Text>
                                        {win && (
                                            <Tooltip title={t('Bring your own license')}>
                                                <Tag color="orange" style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: '16px', paddingInline: 4 }}>BYOL</Tag>
                                            </Tooltip>
                                        )}
                                    </div>
                                    {/* version choice: clicks here only pick a version, never re-toggle the card */}
                                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                                    {famBlock ? <Text type="secondary" style={{ fontSize: 12 }}>{famBlock}</Text> : (
                                    <div onClick={(e) => e.stopPropagation()}>
                                        <Select
                                            size="small"
                                            value={chosen}
                                            onChange={pick}
                                            onDropdownVisibleChange={(o) => { if (o && !active) pick(chosen); }}
                                            options={f.images.map((i) => {
                                                const why = imgBlock(i);
                                                return { value: i.id, disabled: !!why, label: why ? `${i.name} · ${why}` : i.name };
                                            })}
                                            style={{ width: '100%' }}
                                            popupMatchSelectWidth={false}
                                            variant={active ? 'outlined' : 'filled'}
                                            aria-label={t('Version')}
                                        />
                                    </div>
                                    )}
                                </Pick>
                            </Col>
                        );
                    })}
                </Row>
            )}
            {image?.license_notice && <div style={{ marginTop: 8 }}><Text type="warning" style={{ fontSize: 12 }}>{t('Windows requires your own license activation (evaluation: 180 days)')}</Text></div>}
        </Step>
    );

    const loginStep = opts && (
        <Step n={nextStep()} title={t('Login')} compact={isMobile} extra={<Text type="secondary" style={{ fontSize: 12 }}>{t('Username')} <Text code>{loginUser}</Text></Text>}>
            <Space direction="vertical" size={12} style={{ width: '100%', maxWidth: 560 }}>
                {!isWindows && (
                    <Segmented value={auth} onChange={setAuth} options={[{ value: 'password', label: t('Password') }, { value: 'ssh', label: t('SSH Keys') }]} />
                )}
                {sshMode ? (
                    <SshKeyPicker value={sshPick} onChange={setSshPick} />
                ) : (
                    <div>
                        <Input.Password
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            addonAfter={<a onClick={() => setPassword(generatePassword(14))}>{t('Random')}</a>}
                            status={passwordOk ? undefined : 'error'}
                            autoComplete="new-password"
                        />
                        {!passwordOk && <Text type="danger" style={{ fontSize: 12 }}>{t('Password must be 8-64 characters')}</Text>}
                    </div>
                )}
            </Space>
        </Step>
    );

    const addonStep = opts && addonRes.length > 0 && (
        <Step n={nextStep()} title={t('Add-ons')} compact={isMobile}>
            <Space direction="vertical" size={14} style={{ width: '100%' }}>
                {addonRes.map((r) => (
                    <ResourceRow key={r.key} r={r} t={t} narrow={isMobile} value={values[r.key]} onChange={setVal(r.key)} extra={extraTag(r.key)} />
                ))}
            </Space>
        </Step>
    );

    const rowLabel = { display: 'inline-block', minWidth: 72 };
    const netRow = { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' };
    // Plan IPv6 "where available": the quote's node decides; before a quote, the area.
    const planV6 = opts?.plan_ipv6 || {};
    const planV6In = planV6.len > 0 && (quote && !quote.error && quote.plan_ipv6 ? !!quote.plan_ipv6.included : !!planV6.available);
    const advancedNet = opts?.vpc?.available && (
        <Space direction="vertical" size={14} style={{ width: '100%' }}>
            <Space wrap>
                <Text style={rowLabel}>{t('Public IPv4')}</Text>
                <Radio.Group
                    value={publicV4}
                    onChange={(e) => { setPublicV4(e.target.value); if (!e.target.value) setIpv6(0); }}
                    optionType="button"
                    options={[
                        { value: 1, label: natPlan ? t('NAT (shared)') : t('Assign') },
                        {
                            value: 0,
                            disabled: vpcMode === 'none',
                            label: <Tooltip title={vpcMode === 'none' ? t('Join a VPC first') : (quote?.ipv4_bundled_unused ? t('IPv4 is included in the plan price; no discount') : null)}>{t('None')}</Tooltip>,
                        },
                    ]}
                />
            </Space>
            <Space wrap>
                <Text style={rowLabel}>VPC</Text>
                <Radio.Group
                    value={vpcMode}
                    onChange={(e) => { setVpcMode(e.target.value); if (e.target.value === 'none') setPublicV4(1); }}
                    optionType="button"
                    options={[
                        { value: 'none', label: t('None') },
                        { value: 'existing', label: t('Existing'), disabled: !vpcList.length },
                        { value: 'new', label: t('Create'), disabled: !opts.vpc.can_create },
                    ]}
                />
            </Space>
            {vpcMode === 'existing' && (
                <Select
                    value={subnetId}
                    onChange={setSubnetId}
                    placeholder={t('Select a subnet')}
                    status={subnetOk ? undefined : 'error'}
                    style={{ width: '100%' }}
                    options={vpcList.map((v) => ({
                        label: `${v.name} (${v.cidr})`,
                        options: v.subnets.map((sn) => ({ value: sn.id, label: `${v.name} · ${sn.cidr}` })),
                    }))}
                />
            )}
            {vpcMode === 'new' && (
                <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
                    <Input value={vpcName} onChange={(e) => setVpcName(e.target.value)} placeholder={t('VPC name')} maxLength={100} />
                    <Input value={vpcCidr} onChange={(e) => setVpcCidr(e.target.value.trim())} placeholder={opts.vpc.suggest_cidr} addonBefore={t('VPC CIDR')} />
                </Space>
            )}
        </Space>
    );
    const v6Extra = v6Available && (Array.isArray(opts.ipv6.sizes) ? (
        <Select
            value={ipv6}
            onChange={setIpv6}
            style={{ minWidth: 0, width: isMobile ? '100%' : 220 }}
            options={[{ value: 0, label: t('No extra') }, ...opts.ipv6.sizes.map((x) => ({
                value: x.len,
                label: <span>{t('Add')} /{x.len} · <Money amount={x.price / (x.period_months || 1)} currency={PLATFORM_CURRENCY} />{t('/month')}</span>,
            }))]}
        />
    ) : (
        <Space>
            <Switch checked={ipv6 > 0} onChange={(v) => setIpv6(v ? 64 : 0)} />
            <Text type="secondary" style={{ fontSize: 12 }}>
                {t('Add')} /64 · <Money amount={opts.ipv6.unit_price / (opts.ipv6.period_months || 1)} currency={PLATFORM_CURRENCY} /> {t('/month')}
            </Text>
        </Space>
    ));
    const networkStep = opts && (
        <Step n={nextStep()} title={t('Network')} compact={isMobile}>
            <Space direction="vertical" size={14} style={{ width: '100%', maxWidth: 560 }}>
                <div style={netRow}>
                    <Text style={rowLabel}>IPv6</Text>
                    {planV6In ? (
                        <Tag color="green" style={{ marginInlineEnd: 0 }}>{t('Includes {{n}} × /{{len}}', { n: planV6.count || 1, len: planV6.len })}</Tag>
                    ) : (!opts.ipv6?.available && <Text type="secondary">{t('IPv6 not yet available in this region')}</Text>)}
                    {v6Extra}
                    {!planV6In && opts.ipv6?.available && !publicV4 && <Text type="secondary">{t('Requires public IPv4')}</Text>}
                </div>
                <div style={netRow}>
                    <Text style={rowLabel}>{t('Security groups')}</Text>
                    <Select
                        mode="multiple"
                        allowClear
                        maxCount={5}
                        value={sgIds}
                        onChange={setSgIds}
                        placeholder={t('Default security group (auto-created)')}
                        optionFilterProp="label"
                        options={sgs.map((g) => ({ value: g.id, label: g.name }))}
                        style={{ flex: 1, minWidth: isMobile ? '100%' : 200 }}
                    />
                    <a href={userPath('/evm/security-groups')} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{t('Manage')}</a>
                </div>
                {advancedNet && (
                    <Collapse
                        ghost
                        size="small"
                        style={{ margin: '-4px -12px' }}
                        items={[{
                            key: 'net',
                            label: <Text ellipsis style={{ maxWidth: '100%' }}>{netSummary}</Text>,
                            extra: <Text type="secondary" style={{ fontSize: 12 }}>{t('Edit')}</Text>,
                            children: advancedNet,
                        }]}
                    />
                )}
            </Space>
        </Step>
    );

    const nameStep = opts && (
        <Step n={nextStep()} title={t('Instance name')} compact={isMobile}>
            <div style={{ maxWidth: 560 }}>
                <Input
                    value={hostname}
                    onChange={(e) => setHostname(e.target.value.trim())}
                    placeholder={t('Hostname (optional)')}
                    status={hostnameOk ? undefined : 'error'}
                    maxLength={63}
                />
                {!hostnameOk && <Text type="danger" style={{ fontSize: 12 }}>{t('Letters, digits and - only; start with a letter or digit')}</Text>}
            </div>
        </Step>
    );

    const cycleStep = opts && (
        <Step n={nextStep()} title={t('Term')} compact={isMobile}>
            <Row gutter={[10, 10]}>
                {opts.cycles.map((c) => {
                    const months = Number(c.period);
                    const save = monthly && months > 1 ? 1 - Number(c.price) / (Number(monthly.price) * months) : 0;
                    return (
                        <Col key={c.period} {...col4}>
                            <Pick active={period === c.period} onClick={() => setPeriod(c.period)}>
                                <Text strong style={{ display: 'block', paddingRight: 16 }}>{cycleLabel(months)}</Text>
                                <Text style={{ display: 'block' }}><Money amount={c.price} currency={PLATFORM_CURRENCY} /></Text>
                                <Space size={4} wrap>
                                    {months > 1 && <Text type="secondary" style={{ fontSize: 12 }}><Money amount={Number(c.price) / months} currency={PLATFORM_CURRENCY} />{t('/month')}</Text>}
                                    {save >= 0.01 && <Tag color="green" style={{ marginInlineEnd: 0 }}>{t('Save {{n}}%', { n: Math.round(save * 100) })}</Tag>}
                                </Space>
                            </Pick>
                        </Col>
                    );
                })}
                {opts.hourly && (
                    <Col key="hour" {...col4}>
                        <Pick active={period === 'hour'} onClick={() => setPeriod('hour')}>
                            <Text strong style={{ display: 'block', paddingRight: 16 }}>{t('Hourly')}</Text>
                            <Text style={{ display: 'block' }}><Money amount={opts.hourly.rate} currency={PLATFORM_CURRENCY} decimals={4} />{t('/hr')}</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>{t('Delete anytime, traffic per GB')}</Text>
                        </Pick>
                    </Col>
                )}
            </Row>
        </Step>
    );

    const noArea = !openAreas.length;
    const nothing = !areas.length || (noArea && !areas.some((a) => a.state === 'soldout'));
    const showSwitch = hasVds || kind === 'vds';

    return (
        <div style={{ padding: isMobile ? '0 4px' : 0, maxWidth: 1400 }}>
            <PageHeader
                title={kind === 'vds' ? t('Buy VDS') : t('Buy cloud server')}
                onBack={() => navigate(userPath('/evm/vms'))}
                dense={isMobile}
                extra={!isMobile && showSwitch ? kindSwitch : null}
            />
            {isMobile && showSwitch && <div style={{ marginBottom: 12 }}>{kindSwitch}</div>}

            {nothing ? (
                <Card><Empty description={kind === 'vds' ? t('No VDS available') : t('No plans available')} /></Card>
            ) : (
                <Row gutter={16} wrap={!wide}>
                    <Col flex={wide ? 'auto' : undefined} xs={wide ? undefined : 24} style={{ minWidth: 0 }}>
                        {regionStep}
                        {noArea ? (
                            <Alert type="warning" showIcon message={t('Out of capacity, unavailable for now')} />
                        ) : (
                            <>
                                {planStep}
                                <Spin spinning={optsLoading}>
                                    {imageStep}
                                    {loginStep}
                                    {addonStep}
                                    {networkStep}
                                    {nameStep}
                                    {cycleStep}
                                </Spin>
                            </>
                        )}
                    </Col>
                    {wide && (
                        <Col flex="340px">
                            <Card
                                size="small"
                                title={t('Order summary')}
                                style={{ position: 'sticky', top: 80 }}
                                styles={{ body: { display: 'flex', flexDirection: 'column', gap: 12 } }}
                            >
                                {summaryBody}
                                <div style={{ borderTop: `1px solid ${token.colorBorderSecondary}`, paddingTop: 10 }}>{totalBlock}</div>
                                {balanceLine}
                                {buyButton}
                                {blocker && <Text type={blockerIsError ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>{blocker}</Text>}
                            </Card>
                        </Col>
                    )}
                </Row>
            )}

            {!wide && !nothing && !noArea && (
                <>
                    <div
                        style={{
                            position: 'sticky',
                            bottom: 0,
                            zIndex: 10,
                            marginTop: 12,
                            padding: '10px 12px',
                            background: token.colorBgElevated,
                            borderTop: `1px solid ${token.colorBorderSecondary}`,
                            boxShadow: token.boxShadowSecondary,
                            borderRadius: token.borderRadiusLG,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                        }}
                    >
                        <div
                            style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                            role="button"
                            tabIndex={0}
                            onClick={() => setDetailOpen(true)}
                            onKeyDown={(e) => { if (e.key === 'Enter') setDetailOpen(true); }}
                        >
                            <Text strong style={{ fontSize: 20, color: token.colorPrimary }}>
                                {total !== null ? <Money amount={total} currency={PLATFORM_CURRENCY} /> : '—'}
                            </Text>
                            <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontSize: 12 }}>
                                {blocker
                                    ? <Text type={blockerIsError ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>{blocker}</Text>
                                    : <Text style={{ fontSize: 12, color: token.colorLink }}>{t('Price details')} <UpOutlined style={{ fontSize: 10 }} /></Text>}
                            </div>
                        </div>
                        <div style={{ flex: 'none', width: isMobile ? 128 : 180 }}>{buyButton}</div>
                    </div>
                    <Drawer open={detailOpen} onClose={() => setDetailOpen(false)} placement="bottom" height="auto" title={t('Order summary')}>
                        {summaryBody}
                        <div style={{ borderTop: `1px solid ${token.colorBorderSecondary}`, paddingTop: 10, marginTop: 10 }}>{totalBlock}</div>
                        <div style={{ marginTop: 8 }}>{balanceLine}</div>
                        {quote?.error && <Text type="danger" style={{ fontSize: 12 }}>{quote.error}</Text>}
                    </Drawer>
                </>
            )}
        </div>
    );
};

export default PurchaseVm;
